<template>
  <div v-if="localBlocks.length" class="promo-banner critical-component-hide">
    <span
      class="visually-hidden"
      v-text="$string('general.site_header.promo_banner')"
    />

    <transition name="fade">
      <splide class="promo-banner__list" :options="carouselOptions">
        <splide-slide
          v-for="block of localBlocks"
          :key="block.key"
          class="promo-banner__list-item"
        >
          <div class="promo-banner__content">
            <a
              v-if="block.url"
              class="promo-banner__list-item-link text-mobile-paragraph-s-regular"
              :href="block.url"
              v-text="block.text"
            />

            <span
              v-else
              class="promo-banner__list-item-text text-mobile-paragraph-s-regular"
              v-text="block.text"
            />

            <div
              v-if="
                block.show_countdown &&
                block.countdown_date &&
                countdownData[block.key]?.isActive
              "
              class="promo-banner__countdown"
            >
              <span
                class="promo-banner__countdown-text"
                v-text="block.countdown_text"
              ></span>
              <div class="promo-banner__countdown-time">
                <template
                  v-for="(unit, index) of countdownData[block.key]?.display ||
                  []"
                  :key="index"
                >
                  <div class="promo-banner__countdown-unit">
                    <div class="promo-banner__countdown-number">
                      {{ unit.value.toString().padStart(2, '0') }}
                    </div>
                    <div class="promo-banner__countdown-label">
                      {{ unit.label }}
                    </div>
                  </div>
                  <span
                    v-if="
                      index <
                      (countdownData[block.key]?.display?.length || 0) - 1
                    "
                    class="promo-banner__countdown-separator"
                    >:</span
                  >
                </template>
              </div>
            </div>
          </div>
        </splide-slide>
      </splide>
    </transition>
  </div>
</template>

<script>
/**
 * Vue: Promo banner (promo-banner)
 * -----------------------------------------------------------------------------
 * Promo banner template.
 * - Conditionally loaded when section has blocks using Liquid.
 *
 * @param {Array} blocks - Promo blocks.
 * @param {Object} liquid - Liquid section ID, used for theme editor events.
 * @param {Object} timing - Timing section settings.
 *
 */
import { Splide, SplideSlide } from '@splidejs/vue-splide';
import { convertToHandle } from '~/helpers/convert';
import {
  dateTime,
  fetchServerTime,
  getCurrentServerTime,
} from '~/helpers/general';

export default {
  name: 'PromoBanner',

  components: {
    Splide,
    SplideSlide,
  },

  props: {
    blocks: {
      type: Array,
      default: () => [],
    },
    // Needed for theme editor events
    // eslint-disable-next-line vue/no-unused-properties
    liquid: {
      type: Object,
      default: () => ({
        sectionId: '',
      }),
    },
    timing: {
      type: Number,
      default: 3000,
    },
  },

  data() {
    return {
      carouselOptions: {
        arrows: false,
        autoHeight: true,
        autoplay: true,
        interval: this.timing * 1000,
        pagination: false,
        pauseOnFocus: false,
        pauseOnHover: true,
        rewind: true,
        type: 'fade',
      },
      localBlocks: [],
      countdownInterval: null,
      countdownData: {}, // Store countdown data for each block
      resizeObserver: null,
    };
  },

  async mounted() {
    // Initialize server time first
    try {
      await fetchServerTime();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('Failed to fetch server time, using client time:', error);
    }

    this.localBlocks = this.getLocalBlocks();

    // Start countdown timer
    this.startCountdownTimer();

    await this.$nextTick();
    this.setPromoBannerHeight();
    this.observeHeightChanges();
    cnvs.EventBus.emit('cnvs:update-site-header-height');
  },

  beforeUnmount() {
    // Clear countdown interval
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }

    // Disconnect resize observer
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  },

  methods: {
    /**
     * Set promo banner height as CSS variable.
     * - Used to offset the site header offset component.
     */
    setPromoBannerHeight() {
      const promoBanner = this.$el;
      if (promoBanner) {
        const height = promoBanner.offsetHeight;
        document.documentElement.style.setProperty(
          '--promo-banner-height',
          `${height}px`,
        );
      }
    },

    /**
     * Observe height changes to update CSS variable.
     * - Updates when content changes (e.g., countdown updates).
     */
    observeHeightChanges() {
      if (!this.$el) return;

      this.resizeObserver = new ResizeObserver(() => {
        this.setPromoBannerHeight();
      });

      this.resizeObserver.observe(this.$el);
    },

    /**
     * Get local blocks by adding additional details to blocks prop.
     * @returns {Array}
     */
    getLocalBlocks() {
      const blocks = this.blocks
        .map((block, index) => {
          if (
            !block.text ||
            (block.type === 'campaign' && !dateTime(block.start, block.end))
          ) {
            return false;
          }

          return {
            ...block,
            key: `promo-${index}-${convertToHandle(block.text)}`,
          };
        })
        .filter(Boolean);

      // Initialize countdown data for new blocks
      blocks.forEach((block) => {
        if (block.show_countdown && block.countdown_date) {
          this.countdownData[block.key] = this.getCountdownTime(
            block.countdown_date,
          );
        }
      });

      return blocks;
    },

    /**
     * Start countdown timer to update countdown display every second.
     */
    startCountdownTimer() {
      // Clear existing interval
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval);
      }

      // Start new interval using the helper function
      this.countdownInterval = setInterval(() => {
        this.updateCountdownData();
      }, 1000);
    },

    /**
     * Update countdown data for all blocks with countdowns.
     */
    updateCountdownData() {
      this.localBlocks.forEach((block) => {
        if (block.show_countdown && block.countdown_date) {
          this.countdownData[block.key] = this.getCountdownTime(
            block.countdown_date,
          );
        }
      });
    },

    /**
     * Calculate countdown time for a given date.
     * @param {String} countdownDate - ISO date string
     * @returns {Object} - Object with isActive boolean and display string
     */
    getCountdownTime(countdownDate) {
      if (!countdownDate) {
        return { isActive: false, display: [] };
      }

      try {
        // Use the helper function to get current server time
        const currentServerTime = getCurrentServerTime();
        const now = new Date(currentServerTime * 1000);

        // Parse the countdown date properly
        let targetDate;
        if (countdownDate.includes('T')) {
          // If it's already in ISO format (YYYY-MM-DDTHH:MM:SS)
          targetDate = new Date(countdownDate);
        } else {
          // If it's just a date (YYYY-MM-DD), assume end of day
          targetDate = new Date(`${countdownDate}T23:59:59`);
        }

        const timeDifference = targetDate - now;

        // Check if the date is valid
        if (isNaN(targetDate.getTime())) {
          console.error('Invalid countdown date:', countdownDate);
          return { isActive: false, display: [] };
        }

        // If countdown has passed, show empty array
        if (timeDifference <= 0) {
          return { isActive: false, display: [] };
        }

        // Calculate time units
        const days = Math.floor(timeDifference / (1000 * 60 * 60 * 24));
        const hours = Math.floor(
          (timeDifference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
        );
        const minutes = Math.floor(
          (timeDifference % (1000 * 60 * 60)) / (1000 * 60),
        );
        const seconds = Math.floor((timeDifference % (1000 * 60)) / 1000);

        // Format display string with structured data for rendering
        const timeUnits = [];

        if (days > 0) {
          timeUnits.push({ value: days, label: 'Days' });
        }
        if (hours > 0 || days > 0) {
          timeUnits.push({ value: hours, label: 'Hours' });
        }
        timeUnits.push({ value: minutes, label: 'Minutes' });
        timeUnits.push({ value: seconds, label: 'Seconds' });

        return { isActive: true, display: timeUnits };
      } catch (error) {
        console.error('Error calculating countdown:', error);
        return { isActive: false, display: [] };
      }
    },
  },
};
</script>

<style lang="scss">
@import './promo-banner';
@import '@/components/splide';
</style>
